import React, { Component } from "react";
import {
  createStyles,
  Paper,
  Theme,
  Typography,
  WithStyles,
  withStyles,
  Button,
  LinearProgress,
  Avatar,
  Collapse,
} from "@material-ui/core";
import { IconButton, Dialog } from "@material-ui/core";
import { connect } from "react-redux";
import { Close, ExpandLess, ExpandMore } from "@material-ui/icons";
import { HoleParam } from "../../types/hole";
import { Game, IndexedGames } from "../../types/game";
import { Player, HoleScoreInfo, Points } from "../../types/player";
import { GlobalState } from "../../types/globalState";
import { hideDialog } from "../SharedComponents/Dialog";
import Config from "../../config";
import GrossPickerDialog from "../Game/GrossPickerDialog";
import GameActions from "../../actions/games";
import { showAlertDialog } from "../SharedComponents/AlertDialog";
import { Mismatch } from "types/general";
import { isGameStandard, getHandicapDisplay } from "utils";
import PlayerName from "components/SharedComponents/PlayerName";
import CalcTeamWinners from "utils/CalcTeamWinners";
import { sortNumberAscending } from "utils/sortPlayerToFirst";
import { MOBILE_BREAKPOINT } from "../../consts/dialog";
import { withDetector } from "utils/withDetector";
import { Gender } from "types/user";

interface Props extends WithStyles {
  gameId?: string;
  player: Player;
  teammates?: Player[];
  games: IndexedGames;
  game?: any;
  user: any;
  quickScoring?: boolean;
  mismatches?: Mismatch[];
  useCurUserScores?: boolean;
  onBehalfPlayer?: boolean;
  hideScores?: boolean;
  online?: boolean;
  sequentialPaperEntry?: boolean;
  onSelectHoleNumber?(holeNumber: number): void;
  onVerifyPlayer?(playerId: string): void;
  onCloseModernScoreCardDialog?(): void;
  updateScores(
    gameId: string,
    playerId: string,
    scoreId: string,
    holeNumber: number,
    gross: number,
    shots?: number,
    onBehalfPlayer?: boolean,
    offline?: boolean,
  ): void;
}
interface State {
  [inde: string]: any;

  cardName: string;
  holesParams: HoleParam[];
  name: string;
  club: string;
  course: string;
  openPickerDialog: boolean;
  currentHoleNumber: number;
  padExpanded: boolean;
}

class ScoreCardView extends Component<Props, State> {
  calcTeamWinners: CalcTeamWinners | null;
  divRef = React.createRef<HTMLDivElement>();
  cursorHoleNumber = 0;

  constructor(props: Props) {
    super(props);
    this.state = {
      cardName: "",
      holesParams: [],
      name: "",
      club: "",
      course: "",
      openPickerDialog: false,
      currentHoleNumber: 0,
      padExpanded: true,
    };

    const game = this.getGame();
    if (this.props.teammates && game) {
      this.calcTeamWinners = new CalcTeamWinners(
        [...this.props.teammates, this.props.player],
        game.competition.scoringFormat,
        game.competition.playingRules,
      );
    } else this.calcTeamWinners = null;
  }

  componentDidMount = () => {
    if (this.props.sequentialPaperEntry && this.getScoreCard()) {
      const first = this.firstEmptyHoleNumber(this.props.player);
      if (first !== 0) this.setCursorHole(first);
    }
  };

  componentDidUpdate = () => {
    if (this.props.sequentialPaperEntry && this.getCursorHole(this.props.player) === 0 && this.getScoreCard()) {
      const first = this.firstEmptyHoleNumber(this.props.player);
      if (first !== 0) this.setCursorHole(first);
    }
    if (this.divRef.current && this.isAllHolesScored(this.props.player)) {
      this.divRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  summation(holeParams: HoleParam[], key: string): number {
    let sum = 0;
    Object.values(holeParams).forEach((param: HoleParam) => {
      if (param[key]) {
        sum += param[key] as number;
      }
    });
    return sum;
  }

  getStartingHoleIndex = (): number => {
    const { player } = this.props;
    if (player.group && player.group.startingHole) {
      return player.group.startingHole - 1;
    }
    return 0;
  };

  getGame = (): Game | null => {
    const { gameId } = this.props;
    if (gameId) {
      return this.props.games[gameId];
    } else if (this.props.game) {
      return this.props.game;
    } else return null;
  };

  isStandardGame = () => isGameStandard(this.getGame()?.competition.type);

  getPlayerAvatar = (player: Player) => {
    if (player.avatar) {
      return Config.API_URL + player.avatar;
    }
    
    if (player.gender === Gender.Men) {
      return require("../../assets/images/icons/avatar_men.png");
    } else if (player.gender === Gender.Women) {
      return require("../../assets/images/icons/avatar_women.png");
    }
    
    return require("../../assets/images/icons/question_mark_200px.png");
  };

  getScoreCard = () => {
    const { user, player } = this.props;
    const game = this.getGame();
    const startingHoleIndex = this.getStartingHoleIndex();
    if (user && game && game.scoreCards) {
      const cardId = game.players[player._id].teeColor ? game.players[player._id].teeColor.scoreCardId : null;
      let sc: any = { ...Object.values(game.scoreCards)[0] };
      if (cardId) sc = { ...game.scoreCards.find((x: any) => x._id === cardId) };
      if (sc.holesParams) {
        sc.holesParams = [...sc.holesParams.slice(startingHoleIndex), ...sc.holesParams.slice(0, startingHoleIndex)];
      }
      return sc;
    }
    return null;
  };

  getDisplayHoles = (): HoleParam[] => {
    const scoreCard = this.getScoreCard();
    if (!scoreCard || !scoreCard.holesParams) return [];
    return [...scoreCard.holesParams].sort((a: HoleParam, b: HoleParam) => sortNumberAscending(a.number, b.number));
  };

  isHoleEmpty = (holeNumber: number, player: Player): boolean => {
    const holeParam = this.getDisplayHoles().find(h => h.number === holeNumber);
    if (!holeParam) return false;
    const gross = this.getGross(holeParam, player);
    return gross === "" || gross === null || gross === undefined;
  };

  getCursorHole = (player: Player): number => {
    if (this.cursorHoleNumber) return this.cursorHoleNumber;
    if (this.state.currentHoleNumber) return this.state.currentHoleNumber;
    return this.firstEmptyHoleNumber(player);
  };

  setCursorHole = (holeNumber: number, padReady = false) => {
    this.cursorHoleNumber = holeNumber;
    if (padReady) {
      this.setState({ currentHoleNumber: holeNumber, openPickerDialog: false, padExpanded: true });
    } else {
      this.setState({ currentHoleNumber: holeNumber });
    }
  };

  nextEmptyHoleNumber = (player: Player, afterHoleNumber: number, treatAsFilled?: number): number => {
    const holes = this.getDisplayHoles();
    if (!holes.length) return afterHoleNumber;
    const idx = holes.findIndex(h => h.number === afterHoleNumber);
    const start = idx === -1 ? 0 : (idx + 1) % holes.length;
    for (let i = 0; i < holes.length; i++) {
      const hole = holes[(start + i) % holes.length];
      if (treatAsFilled !== undefined && hole.number === treatAsFilled) continue;
      if (this.isHoleEmpty(hole.number, player)) return hole.number;
    }
    return afterHoleNumber;
  };

  nextDisplayHoleNumber = (afterHoleNumber: number): number => {
    const holes = this.getDisplayHoles();
    if (!holes.length) return afterHoleNumber;
    const idx = holes.findIndex(h => h.number === afterHoleNumber);
    if (idx === -1 || idx === holes.length - 1) return afterHoleNumber;
    return holes[idx + 1].number;
  };

  firstEmptyHoleNumber = (player: Player): number => {
    const holes = this.getDisplayHoles();
    if (!holes.length) return 0;
    const empty = holes.find(h => this.isHoleEmpty(h.number, player));
    return empty ? empty.number : holes[holes.length - 1].number;
  };

  getGrossStyle = (holeParam: HoleParam, player: Player) => {
    const par = holeParam.par!;
    const gross = this.getGross(holeParam, player);

    const bColors = ["#ff98ca", "#ffff01", "#fe0000", "#FFF", "#00b054", "#ffc000", "#056ec1", "#7030a0", "#000"];
    const colors = ["#FFFFFF", "#000000", "#FFFFFF", "#000", "#FFFFFF", "#000000", "#FFFFFF", "#FFFFFF", "#FFF"];
    if (gross === "" || gross === null) return {};
    else if (gross === "mismatch") return { backgroundColor: "#cc3f3c", color: "#FFF" };

    const index = gross - par + 3;
    if (colors[index]) {
      return { backgroundColor: bColors[index], color: colors[index] };
    } else {
      return { backgroundColor: "#000", color: "#FFF" };
    }
  };
  getGross(holeParam: HoleParam, player: Player) {
    const { holes, uholes } = player;
    if (!holes || !holes[holeParam.number]) return "";
    if (holes[holeParam.number].mismatch) return "mismatch";

    if (this.props.useCurUserScores) {
      return uholes?.[holeParam.number]?.grosses?.[this.props.user._id] || "";
    }

    if (this.props.onBehalfPlayer) {
      return uholes?.[holeParam.number]?.grosses?.[player._id] || "";
    }

    return holes[holeParam.number].gross;
  }

  sequentialGridCols(holesLength: number) {
    if (holesLength === 9) return 9;
    if (holesLength === 12 || holesLength === 6 || holesLength === 18) return 6;
    return Math.min(holesLength, 6);
  }

  renderSequentialHoles(player: Player, holesParams: HoleParam[]) {
    const { classes } = this.props;
    const holes = [...holesParams].sort((a, b) => sortNumberAscending(a.number, b.number));
    const cols = this.sequentialGridCols(holes.length);
    return (
      <div
        className={classes.flexSequential}
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {holes.map(holeParam => this.renderItem(holeParam, player, player.points.holes[holeParam.number]))}
      </div>
    );
  }

  sequentialTotalCells(holesLength: number, points: Points) {
    const scoringFormat = this.getScoringFormat();
    const isStableford = scoringFormat === Config.SCORING_FORMAT.STABLEFORD;
    const pts = (point: number, nett: number) => (isStableford ? point : nett);

    if (holesLength === 18) {
      return [
        { label: "OUT", gross: points.summary[1].totalGross, pts: pts(points.summary[1].totalPoint, points.summary[1].totalNett) },
        { label: "IN", gross: points.summary[3].totalGross, pts: pts(points.summary[3].totalPoint, points.summary[3].totalNett) },
        { label: "TOTAL", gross: points.totalGross, pts: pts(points.totalPoint, points.totalNett) },
      ];
    }
    if (holesLength === 12) {
      return [
        { label: "OUT", gross: points.summary[0].totalGross, pts: pts(points.summary[0].totalPoint, points.summary[0].totalNett) },
        { label: "IN", gross: points.summary[1].totalGross, pts: pts(points.summary[1].totalPoint, points.summary[1].totalNett) },
        { label: "TOTAL", gross: points.totalGross, pts: pts(points.totalPoint, points.totalNett) },
      ];
    }
    return [{ label: "TOTAL", gross: points.totalGross, pts: pts(points.totalPoint, points.totalNett) }];
  }

  renderSequentialTotals(holesLength: number, points: Points) {
    const { classes } = this.props;
    const cells = this.sequentialTotalCells(holesLength, points);
    const ptsLabel = this.getScoringFormat() === Config.SCORING_FORMAT.STABLEFORD ? "Pts" : "Nett";
    return (
      <div
        className={classes.seqTotals}
        style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}
      >
        {cells.map(cell => (
          <div key={cell.label} className={classes.seqTotalCell}>
            <span className={classes.seqTotalLabel}>{cell.label}</span>
            <span className={classes.seqTotalGross}>
              {cell.gross}
              <span className={classes.seqTotalUnit}>Gross</span>
            </span>
            <span className={classes.seqTotalPts}>
              {cell.pts}
              <span className={classes.seqTotalUnit}>{ptsLabel}</span>
            </span>
          </div>
        ))}
      </div>
    );
  }

  renderTotals(holesLength: number, points: Points) {
    const { classes } = this.props;
    if (this.props.sequentialPaperEntry) return this.renderSequentialTotals(holesLength, points);
    const scoringFormat = this.getScoringFormat();
    const isStableford = scoringFormat === Config.SCORING_FORMAT.STABLEFORD;
    const OUT = <strong>OUT: </strong>;
    const TOTAL = <strong>Total: </strong>;
    const IN = <strong>IN: </strong>;

    switch (holesLength) {
      case 6:
      case 9:
        return (
          <div className={classes.inline}>
            <div>
              <p>
                {TOTAL}
                {points.totalGross}
                <br />
                <label className={classes.points}>
                  {TOTAL}
                  {isStableford ? points.totalPoint : points.totalNett}
                </label>
              </p>
            </div>
          </div>
        );
      case 12:
        return (
          <div className={classes.inline}>
            <div>
              <p>
                {OUT}
                {points.summary[0].totalGross} <br />
                <label className={classes.points}>
                  {OUT}
                  {isStableford ? points.summary[0].totalPoint : points.summary[0].totalNett}
                </label>
              </p>
            </div>
            <div>
              <p>
                {IN}
                {points.summary[1].totalGross}
                <br />
                <label className={classes.points}>
                  {IN}
                  {isStableford ? points.summary[1].totalPoint : points.summary[1].totalNett}
                </label>
              </p>
            </div>
            <div>
              <p>
                {TOTAL}
                {points.totalGross} <br />
                <label className={classes.points}>
                  {TOTAL}
                  {isStableford ? points.totalPoint : points.totalNett}
                </label>
              </p>
            </div>
          </div>
        );
      case 18:
        return (
          <div className={classes.inline}>
            <div>
              <p>
                {OUT}
                {points.summary[1].totalGross}
                <br />
                <label className={classes.points}>
                  {OUT}
                  {isStableford ? points.summary[1].totalPoint : points.summary[1].totalNett}
                </label>
              </p>
            </div>
            <div>
              <p>
                {IN}
                {points.summary[3].totalGross}
                <br />
                <label className={classes.points}>
                  {IN}
                  {isStableford ? points.summary[3].totalPoint : points.summary[3].totalNett}
                </label>
              </p>
            </div>
            <div>
              <p>
                {TOTAL}
                {points.totalGross}
                <br />
                <label className={classes.points}>
                  {TOTAL}
                  {isStableford ? points.totalPoint : points.totalNett}
                </label>
              </p>
            </div>
          </div>
        );
    }
  }

  onHoleNumberClick = (holeNumber: number) => {
    const { quickScoring, sequentialPaperEntry } = this.props;
    if (sequentialPaperEntry) {
      this.setCursorHole(holeNumber, true);
      return;
    }
    if (quickScoring) {
      this.grossPickerDialogStatusChanger(true, holeNumber);
    }

    if (!this.props.onSelectHoleNumber) return;
    this.props.onSelectHoleNumber(holeNumber);
    hideDialog();
  };

  onSubmitClick = (playerId: string) => {
    if (this.props.onVerifyPlayer)
      showAlertDialog(
        "Submit",
        "Are you sure?",
        () => this.props.onVerifyPlayer && this.props.onVerifyPlayer(playerId),
        () => {},
      );

    if (this.props.onCloseModernScoreCardDialog) this.props.onCloseModernScoreCardDialog();
    hideDialog();
  };

  getScoringFormat = () => {
    const game = this.getGame();
    return typeof game?.competition === "object" ? game.competition.scoringFormat.toString() : "undefined";
  };

  calculateHoleNumber(number: number) {
    const scoreCard = this.getScoreCard();
    if (!scoreCard || !scoreCard.name) return number;
    const name = scoreCard.name.toLowerCase();
    const { holesNumber } = scoreCard.course;
    if (name.includes("back")) {
      return number + holesNumber;
    } else if (name.includes("middle")) {
      return number + 6;
    } else return number;
  }

  renderItem(holeParam: any, player: Player, holeScores: HoleScoreInfo) {
    const { classes, sequentialPaperEntry } = this.props;
    const gross = this.getGross(holeParam, player);
    const scoringFormat = this.getScoringFormat();
    const isCurrent = sequentialPaperEntry && holeParam.number === this.getCursorHole(player);
    let playerScores: number[] = [];

    if (this.calcTeamWinners && holeParam.number in player.holes) {
      playerScores = this.calcTeamWinners.calculate(holeParam.number)[this.props.player._id] || [];
    }

    const displayHole = this.calculateHoleNumber(holeParam.number);
    const pointsValue =
      !gross || gross === "mismatch"
        ? null
        : scoringFormat === Config.SCORING_FORMAT.STABLEFORD
        ? holeScores?.point
        : holeScores?.nett;

    if (sequentialPaperEntry) {
      return (
        <button
          key={holeParam.number}
          type="button"
          className={`${classes.seqHole} ${isCurrent ? classes.seqHoleCurrent : ""}`}
          onClick={() => this.onHoleNumberClick(holeParam.number)}
          aria-label={`Hole ${displayHole}`}
          aria-current={isCurrent ? "true" : undefined}
        >
          <span className={`${classes.seqSquare} ${isCurrent ? classes.seqSquareCurrent : ""}`}>
            <span className={classes.seqHoleNo}>{displayHole}</span>
            <span className={classes.seqSi}>{holeParam.si}</span>
            {playerScores.includes(holeParam.number) ? <span className={classes.seqCheck}>✓</span> : null}
          </span>
          <span className={classes.seqPar}>{holeParam.par}</span>
          <span
            className={classes.seqScore}
            style={this.getGrossStyle(holeParam, player)}
            aria-hidden="true"
          >
            {gross === "mismatch" ? <Close style={{ fontSize: 16, margin: 0 }} /> : gross}
          </span>
          <span className={classes.seqPoints}>{pointsValue}</span>
        </button>
      );
    }

    return (
      <div key={holeParam.number} className={classes.holeColumn}>
        <div className={[classes.tile, classes.square, isCurrent ? classes.currentSquare : ""].join(" ")}>
          <span>{displayHole}</span>
          {/* rendering stroke index under hole number */}
          <strong style={{ fontWeight: "bolder", color: "red" }}>{holeParam.si}</strong>
          {/* Only scored holes */}
          <span>{playerScores.includes(holeParam.number) ? "✓" : ""}</span>
        </div>
        <div className={classes.tile} style={{ paddingTop: 8 }}>
          {holeParam.par}
        </div>
        <button
          type="button"
          className={[classes.tile, classes.circle].join(" ")}
          style={{
            ...this.getGrossStyle(holeParam, player),
            ...(isCurrent ? { boxShadow: "0 0 0 3px #5e35b1" } : {}),
          }}
          onClick={() => this.onHoleNumberClick(holeParam.number)}
          aria-label={`Hole ${displayHole}`}
        >
          {gross === "mismatch" ? <Close style={{ margin: -6 }} /> : gross}
        </button>
        <div className={[classes.tile, classes.points].join(" ")}>{pointsValue}</div>
      </div>
    );
  }

  isAllHolesScored = (player: Player) => {
    //this return false if all of player's holes gross are entered
    const holes = Object.values(player.holes);
    const { holesParams } = this.getScoreCard()!;
    if (holesParams.length !== holes.length) return false;

    if (this.props.useCurUserScores || this.props.onBehalfPlayer) {
      const pId = this.props.useCurUserScores ? this.props.user._id : player._id;
      return Object.values(player.uholes).every(
        h => h.grosses[pId] !== undefined && !player.holes[h.holeNumber]?.mismatch,
      );
    }

    return holes.every(item => item.gross !== undefined);
  };

  enterNextPlayerQuickScore = () => {
    if (this.props.quickScoring)
      showAlertDialog(
        "Quick score",
        "Do you want to enter another quick score",
        () => {},
        () => {
          hideDialog();
        },
      );
  };
  renderSubmitButton = (player: any) => {
    const { classes } = this.props;
    //second conditions checks if player is unverifiable
    return player.isUnverifiable || (this.props.quickScoring && this.isAllHolesScored(player)) ? (
      <Button
        variant="contained"
        fullWidth
        color="secondary"
        onClick={() => {
          this.onSubmitClick(player._id);
          // this.enterNextPlayerQuickScore(); // checks wheather to render the quick scoring list or not
        }}
        className={`${classes.okBtn} ${this.props.sequentialPaperEntry ? classes.okBtnSequential : ""}`}
      >
        OK TO SUBMIT
      </Button>
    ) : null;
  };

  grossPickerDialogStatusChanger = (status: boolean, holeNumber?: number) => {
    if (holeNumber !== undefined) this.cursorHoleNumber = holeNumber;
    else if (!status) this.cursorHoleNumber = 0;
    this.setState({ openPickerDialog: status, currentHoleNumber: holeNumber !== undefined ? holeNumber : 0 });
  };

  handleGrossChange = (gameId: string, player: Player, holeNumber: number, gross: any, dnfSelected: boolean) => {
    const hole = holeNumber || this.getCursorHole(player);
    if (!hole) return;
    this.props.updateScores(
      gameId,
      player._id,
      player.scoreId,
      hole,
      dnfSelected ? "DNF" : gross,
      gross,
      this.props.onBehalfPlayer,
      !this.props.online,
    );
    if (this.props.sequentialPaperEntry) {
      if (gross === null) {
        this.setCursorHole(hole, true);
        return;
      }
      const next = this.nextEmptyHoleNumber(player, hole, hole);
      this.setCursorHole(next, true);
      return;
    }
    this.grossPickerDialogStatusChanger(false);
  };

  renderSequentialPicker() {
    if (!this.getScoreCard()) return null;
    const { gameId, player, classes } = this.props;
    const { padExpanded } = this.state;
    const holeNumber = this.getCursorHole(player);
    const displayHole = holeNumber ? this.calculateHoleNumber(holeNumber) : "";
    const clearCurrentHole = () => {
      this.handleGrossChange(gameId ?? "", player, this.getCursorHole(player), null, false);
    };
    return (
      <div className={classes.sequentialPicker}>
        <div className={classes.padToolbar}>
          <div className={classes.holeCue} aria-live="polite" aria-atomic="true">
            <span className={classes.holeCueLabel}>Hole</span>
            <span className={classes.holeCueNumber}>{displayHole}</span>
          </div>
          <button type="button" className={classes.clearInline} onClick={clearCurrentHole}>
            CLEAR SCORE
          </button>
          <button
            type="button"
            className={classes.padToggle}
            onClick={() => this.setState({ padExpanded: !padExpanded })}
            aria-expanded={padExpanded}
            aria-label={padExpanded ? "Hide numpad" : "Show numpad"}
          >
            {padExpanded ? "Hide numpad" : "Show numpad"}
            {padExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </button>
        </div>
        <Collapse in={padExpanded} timeout={200} unmountOnExit>
          <div className={classes.padBody}>
            <GrossPickerDialog
              embedded
              hideClear
              gameId={gameId ?? ""}
              holeNumber={holeNumber}
              onSelect={(gross: any, dnfSelected: boolean, p?: Player) => {
                this.handleGrossChange(gameId ?? "", p || player, this.getCursorHole(p || player), gross, dnfSelected);
              }}
            />
          </div>
        </Collapse>
      </div>
    );
  }

  renderGrossPickerDialog() {
    const { gameId, classes, sequentialPaperEntry } = this.props;
    const { player } = this.props;
    const displayHole = this.getCursorHole(player) ? this.calculateHoleNumber(this.getCursorHole(player)) : "";
    return (
      <Dialog
        hideBackdrop={!sequentialPaperEntry}
        open={this.state.openPickerDialog}
        onBackdropClick={() => this.grossPickerDialogStatusChanger(false)}
        PaperProps={sequentialPaperEntry ? { className: classes.overlayPaper } : undefined}
      >
        <div className={classes.overlayHeader}>
          {sequentialPaperEntry ? (
            <div className={classes.holeCue} aria-live="polite" aria-atomic="true">
              <span className={classes.holeCueLabel}>Hole</span>
              <span className={classes.holeCueNumber}>{displayHole}</span>
            </div>
          ) : (
            <span />
          )}
          {sequentialPaperEntry ? (
            <button
              type="button"
              className={classes.clearInline}
              onClick={() =>
                this.handleGrossChange(gameId ?? "", player, this.getCursorHole(player), null, false)
              }
            >
              CLEAR SCORE
            </button>
          ) : null}
          <IconButton
            className={classes.titleClose}
            size="small"
            onClick={() => this.grossPickerDialogStatusChanger(false)}
            aria-label="Close score pad"
          >
            <Close />
          </IconButton>
        </div>

        <div className={sequentialPaperEntry ? classes.overlayPad : undefined}>
          <GrossPickerDialog
            embedded={!!sequentialPaperEntry}
            hideClear={!!sequentialPaperEntry}
            gameId={gameId ?? ""}
            holeNumber={this.getCursorHole(player)}
            onSelect={(gross: any, dnfSelected: boolean, p?: Player) => {
              this.handleGrossChange(gameId ?? "", p || player, this.getCursorHole(p || player), gross, dnfSelected);
            }}
          />
        </div>
      </Dialog>
    );
  }

  renderPlayerTitle(player: Player, displayHandicap: number) {
    const { classes } = this.props;
    return (
      <div className={classes.playerTitle}>
        <Avatar src={this.getPlayerAvatar(player)} alt={player.name} className={classes.titleAvatar} />
        <div className={classes.titleLine}>
          <span className={classes.titleName}>
            <PlayerName isGuest={player.guest} name={player.name} lastName={player.lastName} />
          </span>
          <span className={classes.titleFact}>{`PH ${getHandicapDisplay(displayHandicap)}`}</span>
          {this.isStandardGame() ? (
            <>
              <span className={classes.titleDot} aria-hidden="true" />
              <span className={classes.titleFact}>{`HI ${player.hci}`}</span>
            </>
          ) : null}
        </div>
        {this.props.onCloseModernScoreCardDialog ? (
          <IconButton
            className={classes.titleClose}
            onClick={this.props.onCloseModernScoreCardDialog}
            aria-label="Close scorecard"
            size="small"
          >
            <Close />
          </IconButton>
        ) : null}
      </div>
    );
  }

  render() {
    let { classes, player, hideScores } = this.props;
    const game = this.getGame();
    if (!game) return null;
    const { holesParams } = this.getScoreCard()!;
    if (!holesParams) return null;
    player = game.players[player._id];
    if (!player.points) return <LinearProgress />;
    console.log({ PlayingHandicap: player.hcp, PlayingHCI: player.hci });

    const displayHandicap = game.competition.handicapRules?.zeroHandicapRule ? 0 : player.hcp;

    const sequential = !!this.props.sequentialPaperEntry;
    const card = (
      <Paper className={`${classes.paper} ${sequential ? classes.paperSequential : ""}`}>
        {player.isUnverifiable ? (
          <div className={classes.topLabel}>
            <label>Tap score to amend</label>
          </div>
        ) : null}
        {sequential ? null : (
          <div className={classes.header}>
            <div className={classes.headerText}>
              <Typography variant={"h5"} className={classes.title}>
                <PlayerName isGuest={player.guest} name={player.name} lastName={player.lastName} />
              </Typography>
              <Typography variant="body2" className={classes.handicapInfo}>
                {`PH ${getHandicapDisplay(displayHandicap)} ${this.isStandardGame() ? `- HI ${player.hci}` : ""}`}
              </Typography>
            </div>
            <Avatar src={this.getPlayerAvatar(player)} alt={player.name} className={classes.profileAvatar} />
          </div>
        )}
        {hideScores ? null : sequential ? (
          <div className={classes.seqDesktopSplit}>
            {this.renderSequentialPicker()}
            <div
              className={`${classes.seqSheet}${
                this.sequentialGridCols(holesParams.length) === 9 ? ` ${classes.seqSheetNine}` : ""
              }`}
            >
              {this.renderSequentialHoles(player, holesParams)}
              {this.props.mismatches
                ? this.props.mismatches.map(mismatch => (
                    <div
                      key={mismatch.hole}
                      style={{ marginBottom: 20, display: "flex", justifyContent: "space-between" }}
                    >
                      <div>
                        <Typography>
                          <span>{mismatch.marker.name}</span>
                          <span>&nbsp;(Marker) scores&nbsp;</span>
                          <span>{mismatch.marker.score}</span>
                        </Typography>
                        <Typography>
                          <span>{mismatch.player.name}</span>
                          <span>&nbsp;(Player) scores&nbsp;</span>
                          <span>{mismatch.player.score}</span>
                        </Typography>
                      </div>
                      <Typography style={{ color: "red", marginLeft: 5 }}>Please confirm you are correct</Typography>
                    </div>
                  ))
                : null}
              <div className={classes.totalsSequential}>
                {this.renderTotals(holesParams.length, player.points)}
              </div>
              {this.renderSubmitButton(player)}
            </div>
            {this.renderGrossPickerDialog()}
          </div>
        ) : (
          <>
            <div className={classes.flex}>
              {holesParams
                .sort((a: any, b: any) => sortNumberAscending(a.number, b.number))
                .map((holeParam: HoleParam) =>
                  this.renderItem(holeParam, player, player.points.holes[holeParam.number]),
                )}
            </div>
            {this.props.mismatches
              ? this.props.mismatches.map(mismatch => (
                  <div
                    key={mismatch.hole}
                    style={{ marginBottom: 20, display: "flex", justifyContent: "space-between" }}
                  >
                    <div>
                      <Typography>
                        <span>{mismatch.marker.name}</span>
                        <span>&nbsp;(Marker) scores&nbsp;</span>
                        <span>{mismatch.marker.score}</span>
                      </Typography>
                      <Typography>
                        <span>{mismatch.player.name}</span>
                        <span>&nbsp;(Player) scores&nbsp;</span>
                        <span>{mismatch.player.score}</span>
                      </Typography>
                    </div>
                    <Typography style={{ color: "red", marginLeft: 5 }}>Please confirm you are correct</Typography>
                  </div>
                ))
              : null}
            <div>{this.renderTotals(holesParams.length, player.points)}</div>
            {this.renderSubmitButton(player)}
            {this.renderGrossPickerDialog()}
          </>
        )}
        <span ref={this.divRef}>&nbsp;</span>
      </Paper>
    );

    if (!sequential) return card;

    return (
      <div className={classes.sequentialShell}>
        {this.renderPlayerTitle(player, displayHandicap)}
        {card}
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      display: "block",
      position: "relative",
      left: "50%",
      transform: "translateX(-50%)",
      width: "auto",
      backgroundColor: "rgba(255,255,552,0.85)",
      padding: theme.spacing(2),
    },
    sequentialShell: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minWidth: 0,
      width: "100%",
      minHeight: "100%",
      height: "100%",
      backgroundColor: "#fff",
    },
    seqDesktopSplit: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        flexDirection: "row-reverse",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: 28,
        padding: "8px 8px 12px",
      },
    },
    seqSheet: {
      minWidth: 0,
      flex: 1,
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        flex: "0 0 520px",
        width: 520,
        maxWidth: 520,
      },
    },
    seqSheetNine: {
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        flex: "0 0 640px",
        width: 640,
        maxWidth: 640,
      },
    },
    playerTitle: {
      position: "sticky",
      top: 0,
      zIndex: 4,
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 44,
      padding: "8px 12px 8px 16px",
      backgroundColor: "#fff",
      boxShadow: "0 1px 0 rgba(15, 23, 42, 0.08)",
    },
    titleAvatar: {
      width: 28,
      height: 28,
      flexShrink: 0,
      boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.12)",
    },
    titleLine: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
      flex: 1,
      overflow: "hidden",
    },
    titleName: {
      fontSize: "0.9375rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#1a1a1a",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    titleFact: {
      fontSize: "0.75rem",
      fontWeight: 600,
      lineHeight: 1.2,
      color: "rgba(0, 0, 0, 0.52)",
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      flexShrink: 0,
    },
    titleDot: {
      width: 4,
      height: 4,
      borderRadius: "50%",
      backgroundColor: "rgba(0, 0, 0, 0.28)",
      flexShrink: 0,
    },
    titleClose: {
      marginLeft: "auto",
      flexShrink: 0,
    },
    sequentialPicker: {
      position: "relative",
      zIndex: 1,
      marginBottom: 16,
      padding: "0 0 16px",
      backgroundColor: "#fff",
      boxShadow: "0 1px 0 rgba(15, 23, 42, 0.08)",
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        flex: "0 0 272px",
        width: 272,
        marginBottom: 0,
        padding: "4px 0 0 20px",
        boxShadow: "none",
        borderRight: "none",
        borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
        position: "sticky",
        top: 8,
      },
    },
    padToolbar: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 36,
      paddingLeft: 6,
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        paddingLeft: 0,
        flexWrap: "wrap",
      },
    },
    clearInline: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      minHeight: 36,
      margin: 0,
      padding: "0 8px",
      border: "none",
      borderRadius: 6,
      backgroundColor: "transparent",
      color: "#b71c1c",
      fontSize: "0.8125rem",
      fontWeight: 700,
      letterSpacing: "0.06em",
      fontFamily: "inherit",
      cursor: "pointer",
      WebkitAppearance: "none",
      appearance: "none",
      whiteSpace: "nowrap",
      "&:hover": {
        color: "#b71c1c",
        backgroundColor: "rgba(183, 28, 28, 0.06)",
      },
      "&:focus-visible": {
        outline: "2px solid rgba(94, 53, 177, 0.5)",
        outlineOffset: 2,
      },
    },
    padToggle: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      flexShrink: 0,
      marginLeft: "auto",
      minWidth: 40,
      minHeight: 36,
      padding: "0 8px",
      border: "none",
      borderRadius: 6,
      backgroundColor: "transparent",
      color: "rgba(0, 0, 0, 0.78)",
      fontSize: "0.8125rem",
      fontWeight: 700,
      fontFamily: "inherit",
      whiteSpace: "nowrap",
      cursor: "pointer",
      WebkitAppearance: "none",
      appearance: "none",
      "&:hover": {
        color: "rgba(0, 0, 0, 0.92)",
      },
      "&:focus-visible": {
        outline: "2px solid rgba(94, 53, 177, 0.5)",
        outlineOffset: 2,
      },
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        display: "none",
      },
    },
    padBody: {
      paddingTop: 10,
      display: "flex",
      justifyContent: "center",
      width: "100%",
    },
    holeCue: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexShrink: 0,
    },
    holeCueLabel: {
      fontSize: "0.75rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "rgba(0, 0, 0, 0.68)",
    },
    holeCueNumber: {
      fontSize: "1.25rem",
      fontWeight: 800,
      lineHeight: 1,
      color: "#1a1a1a",
      fontVariantNumeric: "tabular-nums",
      minWidth: "1.25em",
    },
    paperSequential: {
      flex: 1,
      padding: "8px 16px 20px",
      left: "auto",
      transform: "none",
      width: "100%",
      backgroundColor: "#fff",
      boxShadow: "none",
      overflow: "auto",
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        padding: "12px 20px 24px",
      },
    },
    overlayPaper: {
      borderRadius: 16,
      overflow: "hidden",
    },
    overlayHeader: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      padding: "4px 8px 4px 12px",
      backgroundColor: "#fff",
    },
    overlayPad: {
      display: "flex",
      justifyContent: "center",
      padding: "8px 12px 16px",
      backgroundColor: "#fff",
    },
    currentSquare: {
      borderWidth: "3px",
      backgroundColor: "rgba(51, 161, 54, 0.12)",
    },
    totalsSequential: {
      marginTop: 16,
      paddingTop: 14,
      borderTop: "1px solid rgba(15, 23, 42, 0.08)",
    },
    seqTotals: {
      display: "grid",
      gap: 8,
    },
    seqTotalCell: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 2,
      minWidth: 0,
    },
    seqTotalLabel: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "rgba(0, 0, 0, 0.42)",
    },
    seqTotalGross: {
      display: "inline-flex",
      alignItems: "baseline",
      gap: 4,
      fontSize: 18,
      fontWeight: 800,
      lineHeight: 1.1,
      color: "#1a1a1a",
      fontVariantNumeric: "tabular-nums",
    },
    seqTotalPts: {
      display: "inline-flex",
      alignItems: "baseline",
      gap: 4,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#f50057",
      fontVariantNumeric: "tabular-nums",
    },
    seqTotalUnit: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "rgba(0, 0, 0, 0.45)",
    },
    seqHole: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
      margin: 0,
      padding: "10px 8px 12px",
      border: "none",
      borderRadius: 8,
      backgroundColor: "transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      WebkitAppearance: "none",
      appearance: "none",
      transition: "background-color 0.12s cubic-bezier(0.23, 1, 0.32, 1), transform 0.12s cubic-bezier(0.23, 1, 0.32, 1)",
      "&:hover": {
        backgroundColor: "rgba(15, 23, 42, 0.04)",
      },
      "&:active": {
        transform: "scale(0.97)",
      },
      "&:focus-visible": {
        outline: "2px solid rgba(94, 53, 177, 0.5)",
        outlineOffset: 1,
      },
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
        "&:active": {
          transform: "none",
        },
      },
    },
    seqHoleCurrent: {
      backgroundColor: "rgba(94, 53, 177, 0.2)",
      padding: "12px 10px 14px",
      "&:hover": {
        backgroundColor: "rgba(94, 53, 177, 0.26)",
      },
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        padding: "14px 10px 16px",
        borderRadius: 10,
      },
    },
    seqSquare: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      width: "100%",
      maxWidth: 56,
      minHeight: 48,
      boxSizing: "border-box",
      padding: "5px 2px 4px",
      border: "2px solid rgba(51, 161, 54, 0.9)",
      borderRadius: 8,
      backgroundColor: "rgba(51, 161, 54, 0.07)",
      lineHeight: 1,
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        maxWidth: "none",
        minHeight: 52,
      },
    },
    seqSquareCurrent: {
      backgroundColor: "rgba(51, 161, 54, 0.32)",
    },
    seqHoleNo: {
      fontSize: 16,
      fontWeight: 800,
      color: "#1a1a1a",
      fontVariantNumeric: "tabular-nums",
      letterSpacing: 0,
    },
    seqSi: {
      fontSize: 13,
      fontWeight: 700,
      color: "#d32f2f",
      fontVariantNumeric: "tabular-nums",
    },
    seqCheck: {
      fontSize: 11,
      fontWeight: 700,
      color: "#33a136",
    },
    seqPar: {
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1,
      color: "rgba(0, 0, 0, 0.5)",
      fontVariantNumeric: "tabular-nums",
    },
    seqScore: {
      width: 34,
      height: 34,
      minWidth: 34,
      maxWidth: 34,
      minHeight: 34,
      maxHeight: 34,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "2px solid #000",
      borderRadius: "50%",
      fontSize: 15,
      fontWeight: 800,
      lineHeight: 1,
      fontVariantNumeric: "tabular-nums",
      fontFamily: "inherit",
      color: "inherit",
      backgroundColor: "transparent",
      pointerEvents: "none",
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        width: 42,
        height: 42,
        minWidth: 42,
        maxWidth: 42,
        minHeight: 42,
        maxHeight: 42,
        fontSize: 16,
      },
    },
    seqPoints: {
      minHeight: 16,
      fontSize: 13,
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#f50057",
      fontVariantNumeric: "tabular-nums",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: theme.spacing(2),
    },
    headerText: {
      flex: 1,
    },
    title: {
      marginBottom: theme.spacing(0.5),
    },
    handicapInfo: {
      color: "rgba(0, 0, 0, 0.6)",
    },
    profileAvatar: {
      width: 80,
      height: 80,
      marginLeft: theme.spacing(2),
      border: "2px solid #33a136",
    },
    flexSequential: {
      display: "grid",
      gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
      width: "100%",
      columnGap: 8,
      rowGap: 10,
      [`@media (min-width: ${MOBILE_BREAKPOINT + 1}px)`]: {
        columnGap: 10,
        rowGap: 12,
      },
    },
    holeColumn: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: 42,
      flex: "0 0 42px",
    },
    inline: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    flex: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",

      "& > div": {
        minWidth: "11%",
        marginBottom: 10,

        [theme.breakpoints.down("sm")]: {
          minWidth: "15%",
        },
      },
    },
    tile: {
      width: 42,
      height: 42,
      boxSizing: "border-box",
      paddingTop: 8,
      margin: 0,
      fontSize: 16,
      textAlign: "center",
      fontWeight: "normal",
      flexShrink: 0,
    },
    points: {
      color: "#f50057",
    },
    square: {
      border: "solid",
      borderColor: "#33a136",
      borderWidth: "2px",
      padding: 3,
      display: "flex",
      flexDirection: "column",

      "& > span": {
        lineHeight: "1",

        "&:last-child": {
          color: "#33a136",
        },
      },
    },
    circle: {
      width: 42,
      height: 42,
      minWidth: 42,
      maxWidth: 42,
      minHeight: 42,
      maxHeight: 42,
      padding: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "solid",
      borderColor: "#000",
      borderWidth: "2px",
      borderRadius: "50%",
      cursor: "pointer",
      lineHeight: 1,
      fontVariantNumeric: "tabular-nums",
      fontFamily: "inherit",
      fontSize: 16,
      color: "inherit",
      backgroundColor: "transparent",
      WebkitAppearance: "none",
      appearance: "none",
    },
    topLabel: {
      textAlign: "center",
      padding: 5,
      backgroundColor: "#FFAF0F",
      marginBottom: 10,
      color: "#ffffff",
      borderRadius: 5,
    },
    okBtn: {
      [theme.breakpoints.down("sm")]: {
        position: "sticky",
        bottom: 20,
        left: 0,
        right: 0,
      },
    },
    okBtnSequential: {
      marginTop: 12,
      [theme.breakpoints.down("sm")]: {
        bottom: 8,
      },
    },
  });
const mapStateToProps = (state: GlobalState) => {
  const { joinedGames, organisedByMe } = state.games;
  const { user } = state.auth;
  return {
    user,
    games: { ...organisedByMe, ...joinedGames },
  };
};

export default withDetector(
  connect(mapStateToProps, { updateScores: GameActions.updateScores })(withStyles(styles)(ScoreCardView)),
);
